/* eslint-disable max-lines */
import { CREDENTIAL_PROVISIONABLE_QUALIFICATIONS } from "@clipboard-health/contract-backend-main";
import { objectId } from "@clipboard-health/contract-core";
import {
  schedulerAgentFeatureFlagSchema,
  workerAttritionManagementFeatureFlagSchema,
  workplaceAnalyticsFeatureFlagSchema,
  workplaceFeedbackPromptFeatureFlagSchema,
} from "@clipboard-health/flag-backend-main";
import { z } from "zod";

import { AnalyticsType } from "../Analytics/constants";
import { VisitType, WorkerRequirements } from "../ExperimentalHomeHealth/Cases/types";
import { iconTypeSchema } from "../redesign/components/Icon.types";
import { CbhFeatureFlag } from "./CbhFeatureFlag";
import { spendingControlsConfigurationSchema } from "./spendingControlsConfiguration";

// eslint-disable-next-line no-restricted-syntax
export enum CbhLiteTrialState {
  OFF = "off",
  INVITE = "invite",
  ACTIVE = "active",
}

// eslint-disable-next-line no-restricted-syntax
export enum GenericOutageBannerSeverity {
  ERROR = "error",
  INFO = "info",
  WARNING = "warning",
  SUCCESS = "success",
}

// eslint-disable-next-line no-restricted-syntax
export enum BannerSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

// eslint-disable-next-line no-restricted-syntax
export enum BannerDisplayTarget {
  ADMIN = "admin",
  FACILITY = "facility",
}

// eslint-disable-next-line no-restricted-syntax
export enum PlacementAccessControlStatus {
  OFF = "off",
  VIEW = "view",
  EDIT_PAID = "edit",
  EDIT_FREE = "edit-free",
  LANDING_PAGE = "landing-page",
}

// eslint-disable-next-line no-restricted-syntax
export enum DonutDeliveryIncentiveType {
  DONUTS = "DONUTS",
}

// eslint-disable-next-line no-restricted-syntax
export enum MissedPunchRequestStage {
  OFF = "OFF",
  NON_INSTANT_PAY_SHIFTS = "NON_INSTANT_PAY_SHIFTS",
  NON_IP_AND_VERIFIED_SHIFTS = "NON_IP_AND_VERIFIED_SHIFTS",
  ALL_SHIFTS = "ALL_SHIFTS",
}

// eslint-disable-next-line no-restricted-syntax
export enum ExtendShiftState {
  DISABLED = "disabled",
  SHIFT_EXTENSION_ENABLED = "shift-extension-enabled",
}

const extendShiftMinimalVersionFields = {
  minimalOtaVersion: z.union([z.number(), z.literal("*")]),
  minimalStoreVersion: z.string(),
};

const extendShiftReleaseSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal(ExtendShiftState.DISABLED),
    ...extendShiftMinimalVersionFields,
  }),
  z.object({
    state: z.literal(ExtendShiftState.SHIFT_EXTENSION_ENABLED),
    ...extendShiftMinimalVersionFields,
    maxExtensionInMinutes: z.number(),
  }),
]);

const chartingRecallConfigurationSchema = z.object({
  autoExpireRecallDurationInDays: z.number().default(28),
  minimumIncompleteChartingNotesLength: z.number().default(10),
  shiftAgeThresholdForRecallInDays: z.number().default(7),
});

export type ChartingRecallConfiguration = z.infer<typeof chartingRecallConfigurationSchema>;

const limitedBasketSizeConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    scoreThreshold: z.number().default(150),
    basketSize: z.number().default(1),
    leadTimeThresholdInHrs: z.number().default(120),
    shiftRequirements: z.array(z.string()).default(["CNA", "LVN", "RN", "NURSE", "CAREGIVER"]),
  }),
]);

const warningBasketSizeConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    scoreThreshold: z.number().default(100),
    basketSize: z.number().default(1),
    leadTimeThresholdInHrs: z.number().default(120),
    failRestrictionDurationInDays: z.number().default(180),
    shiftRequirements: z.array(z.string()).default(["CNA", "LVN", "RN", "NURSE", "CAREGIVER"]),
  }),
]);

const orientationShiftsConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    isRequiredForBooking: z.boolean().default(true),
    enabledForUrgentShifts: z.boolean().default(false),
    requiredForRequirements: z.array(z.string()).default(["CNA", "LVN"]),
    kind: z.enum(["single-shift", "block-shifts", "pre-shift"]),
  }),
]);

const starredProfessionalConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
  }),
]);

export const facilityDashboardBannerSchema = z.object({
  id: z.string().nullable().optional(),
  type: z.nativeEnum(GenericOutageBannerSeverity),
  size: z.nativeEnum(BannerSize),
  title: z.string().optional(),
  titleTextColor: z.string().nullable().optional(),
  titleWeight: z.string().nullable().optional(),
  description: z.string().optional(),
  buttonText: z.string().optional(),
  buttonLink: z.string().optional(),
  buttonSize: z.enum(["small", "medium", "large"]).optional(),
  buttonColor: z.string().optional(),
  buttonTextColor: z.string().optional(),
  hideIcon: z.boolean().optional(),
  imageUrl: z.string().optional(),
  imageSize: z.string().optional(),
  imageVerticalOffset: z.string().optional(),
  allowImageOverflow: z.boolean().optional(),
  textColor: z.string().nullable().optional(),
  displayTargets: z.array(z.nativeEnum(BannerDisplayTarget)),
  icon: iconTypeSchema.nullable().optional(),
  iconWidth: z.string().nullable().optional(),
  iconHeight: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  isDismissible: z.boolean().optional(),
});

export type FacilityDashboardBanner = z.infer<typeof facilityDashboardBannerSchema>;

const visitRecommendedRateParameters = z.object({
  perVisit: z.object({
    "RN-SOC": z.number(),
    "RN-SOC-And-Regular": z.number(),
    "RN-Recert": z.number(),
    "RN-Regular": z.number(),
    "RN-Supervisory": z.number(),
    "RN-Discharge": z.number(),
    "RN-ROC": z.number(),
    LVN: z.number(),
    Caregiver: z.number(),
    CNA: z.number(),
    CHHA: z.number(),
  }),
  perHour: z.object({
    RN: z.number(),
    LVN: z.number(),
    Caregiver: z.number(),
    CNA: z.number(),
    CHHA: z.number(),
  }),
});
export type VisitRecommendedRateParameters = z.infer<typeof visitRecommendedRateParameters>;

export const cbhFeatureFlags = {
  [CbhFeatureFlag.WORKPLACE_VISIT_HEAT_MAP]: z.boolean(),
  [CbhFeatureFlag.CONNECT_EMR]: z.boolean(),
  [CbhFeatureFlag.EMAIL_OTP_INSTEAD_OF_MAGIC_LINK]: z.boolean(),
  [CbhFeatureFlag.REDESIGNED_WORKPLACE_LOGIN_FLOW]: z.boolean(),
  [CbhFeatureFlag.WORKER_ATTRITION_MANAGEMENT]: workerAttritionManagementFeatureFlagSchema,
  [CbhFeatureFlag.USERS_ALLOWED_TO_OVERRIDE_SHIFT_ASSIGNMENT_BLOCKERS]: z.boolean(),
  [CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS]: spendingControlsConfigurationSchema,
  [CbhFeatureFlag.ROLLOUT_WORKPLACE_RULES_WITH_QUIZ]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.WORKPLACE_TO_WORKER_REFERRALS]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.DONUT_DELIVERY_INCENTIVE_FOR_BLOCKING_WORKERS]: z.object({
    isEnabled: z.boolean(),
    coolDownInHours: z.number(),
    numberOfDonuts: z.number(),
  }),
  [CbhFeatureFlag.MASS_SHIFT_UPLOAD]: z.boolean(),
  [CbhFeatureFlag.SHOULD_SHOW_WORKER_ETA]: z.boolean(),
  [CbhFeatureFlag.BASE_RATE_PAY_FILTERS]: z.number(),
  [CbhFeatureFlag.BASE_RATE_MULTIPLE_PAY_FILTERS]: z.number(),
  [CbhFeatureFlag.NLC_STATES]: z.object({
    active_states: z.array(z.string()),
  }),
  [CbhFeatureFlag.PERSONAL_ID_SUBTYPES]: z.object({
    manual: z.array(z.string()),
    stripe: z.array(z.string()),
  }),
  [CbhFeatureFlag.WORKPLACE_REQUEST_NEW_WORKER_REQUIREMENT_RATE]: z.boolean(),
  [CbhFeatureFlag.WORKER_RATINGS_CAROUSEL]: z.object({
    frequencyInDays: z.number().optional(),
  }),
  [CbhFeatureFlag.SUPPORT_LINKS]: z.record(z.string(), z.string()),
  [CbhFeatureFlag.CALIFORNIA_TIMESHEET]: z.boolean(),
  [CbhFeatureFlag.WORKER_SERVICE_FOR_ADMIN_PANEL]: z.boolean(),
  [CbhFeatureFlag.WORKER_SERVICE_MIGRATED_API_MAP]: z.record(z.string(), z.boolean()),
  [CbhFeatureFlag.VARIABLE_CHARGE_STANDARD]: z.boolean(),
  [CbhFeatureFlag.RECOMMENDED_RATE_FOR_CHARGE_BLOCKED_FACILITIES]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION]: z.object({
    global: z.boolean().optional(),
    id: z.array(z.string()).optional(),
    msa: z.array(z.string()).optional(),
    state: z.array(z.string()).optional(),
  }),
  [CbhFeatureFlag.MY_ACCOUNT_PAGE]: z.boolean(),
  [CbhFeatureFlag.RATE_NEGOTIATION_LIMITS]: z.record(z.string(), z.string()),
  [CbhFeatureFlag.RATE_NEGOTIATION_DISPLAY_WORKER_RATING]: z.boolean(),
  [CbhFeatureFlag.MARKETING_CONVERSION_QUALIFICATIONS_WHITELIST]: z.object({
    qualifications: z.array(z.string()),
  }),
  [CbhFeatureFlag.BULK_UPDATE_EMPLOYEE_ROLES]: z.boolean(),
  [CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMIZATION]: z.boolean(),
  [CbhFeatureFlag.MAKE_RATING_FEEDBACK_REQUIRED]: z.boolean(),
  [CbhFeatureFlag.SHOULD_SHOW_LIVE_MAP_VIEW]: z.boolean(),
  [CbhFeatureFlag.AUTO_EXPAND_SHIFTS_PANEL_IN_CALENDAR_PAGE]: z.boolean(),
  [CbhFeatureFlag.POST_SHIFT_OPTIONS_AB_TESTING]: z.object({
    orders: z.array(z.enum(["block-shifts", "per-diem"])),
  }),
  [CbhFeatureFlag.HCF_ONBOARDING]: z.object({
    minimumWorkplaceCreationDate: z.string().optional(),
    daysUntilOnboardingOverdue: z.number().optional(),
  }),
  [CbhFeatureFlag.WORKPLACE_BLOCK_SHIFTS_TYPE_FORMS]: z.object({
    intermediate: z.string(),
    worker: z.string(),
  }),
  [CbhFeatureFlag.USP_CLIENT_SETTINGS]: z.object({
    enabled: z.boolean(),
    leadTime: z.number(),
  }),
  [CbhFeatureFlag.IN_APP_CHAT_LIMITED_DAYS_AFTER_BOOKED_SHIFT]: z.object({
    days: z.number().optional(),
  }),
  [CbhFeatureFlag.SHOW_RATE_NEGOTIATION_CONTROL_TOGGLE]: z.boolean(),
  [CbhFeatureFlag.ATTENDANCE_RATE_CONFIG]: z.object({
    numberOfDays: z.number(),
  }),
  [CbhFeatureFlag.VARIABLE_CHARGE_REVAMPED_ACCESS_CONTROL]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_SELF_SIGNUP]: z.object({
    selfSignUpApiEnabled: z.boolean().optional(),
    signContractJotForm: z.string().optional(),
    suspiciousDomains: z.array(z.string()).optional(),
    potentialHCPQualifications: z.array(z.string()).optional(),
    showResendEmailCTA: z.boolean().optional(),
  }),
  [CbhFeatureFlag.SHOULD_SHOW_CONTRACT_TERMS]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_REFERRALS]: z.object({
    workplaceToWorkplace: z.boolean(),
    workplaceToWorkplaceVariant: z.enum(["shift_credit", "shift_credit_and_user_bonus"]).optional(),
    workplaceToWorkplaceIncentive: z.record(z.string(), z.number()).optional(),
  }),
  [CbhFeatureFlag.REMOVE_ADDED_MANUAL_PAY_WHEN_MANUAL_CHARGE]: z.object({
    include: z.array(z.string()),
  }),
  [CbhFeatureFlag.AVAILABLE_WORKERS_SHIFT_INFO_TEXT]: z.string(),
  [CbhFeatureFlag.MAPLE_INTEGRATION]: z.object({
    allApisThroughBgJobs: z.boolean(),
    enableApi: z.boolean(),
    enableWebhook: z.boolean(),
  }),
  [CbhFeatureFlag.ADMIN_APP_HIDE_PAYROLL_TABLE]: z.boolean(),
  [CbhFeatureFlag.UI_STRIPE_TRANSFERS]: z.boolean(),
  [CbhFeatureFlag.WORKER_BLOCK_BOOKING]: z.boolean(),
  [CbhFeatureFlag.LD_UPGRADE_TESTING_WITH_CONTEXT]: z.boolean(),
  [CbhFeatureFlag.EDIT_SHIFT_TIME_RESTRICTION]: z.boolean(),
  [CbhFeatureFlag.AUTOMATIC_TIME_SLOT_UPDATE]: z.boolean(),
  [CbhFeatureFlag.VIEW_SHIFT_CHARGE_RATE]: z.boolean(),
  [CbhFeatureFlag.MAXIMUM_ALLOWED_WORKPLACE_CHARGE_RATES]: z.record(z.string(), z.number()),
  [CbhFeatureFlag.SHIFT_INVITE_CONFIGURATION]: z.object({
    expirationTime: z.object({
      defaultBookingTimeInMinutes: z.number(),
      ranges: z.array(
        z.object({
          bookingTimeInMinutes: z.number(),
          maxLeadTimeInHours: z.number(),
        })
      ),
    }),
    notifications: z.object({
      aboutToExpireTimeInMinutes: z.number(),
    }),
  }),
  [CbhFeatureFlag.ROLLOUT_CHAT_CREATION]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_BLOCK_BOOKING]: z.object({
    enablePriorityAccess: z.boolean().optional(),
    enableInstantBook: z.boolean().optional(),
    showShiftDatesInfo: z.boolean().optional(),
    isBlockBookingEnabled: z.boolean().optional(),
  }),
  [CbhFeatureFlag.ROLLOUT_QUIZ_V2_FACILITY_RULES_AI]: z.boolean(),
  [CbhFeatureFlag.RELEASE_REDESIGNED_RULES_AND_EXPECTATIONS_ADMIN_PAGE]: z.boolean(),
  [CbhFeatureFlag.SHOW_UPDATED_DNR_COPY]: z.boolean(),
  [CbhFeatureFlag.ENABLE_SHIFT_SPECIFICATION]: z.boolean(),
  [CbhFeatureFlag.UNFILLED_PAST_SHIFT_DELETION]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_ONBOARDING_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_PAY_RATES]: z.record(
    z.nativeEnum(VisitType),
    z.record(z.nativeEnum(WorkerRequirements), z.number())
  ),
  [CbhFeatureFlag.HOME_HEALTH_WORKER_CHAT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_FOOTER_URLS]: z.object({
    demoUrl: z.string(),
    faqUrl: z.string(),
  }),
  [CbhFeatureFlag.HOME_HEALTH_BONUS_PAYMENT]: z.boolean(),
  [CbhFeatureFlag.NON_WORKER_CLOCK_IN]: z.boolean(),
  [CbhFeatureFlag.SHOW_AVAILABLE_WORKERS_CONFIG]: z.object({
    qualifications: z.array(z.string()),
  }),
  [CbhFeatureFlag.HOME_HEALTH_PT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_OCCUPATIONAL_THERAPIST_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_SOCIAL_WORKER_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_BIWEEKLY_VISITS_ENABLED]: z.boolean(),
  [CbhFeatureFlag.CALENDAR_POPOVER_ENABLED]: z.boolean(),
  [CbhFeatureFlag.SHIFT_INVITE_CUSTOM_RATE_BOOST]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_MENU_ITEMS_HIDDEN]: z.boolean(),
  [CbhFeatureFlag.SHIFT_INVITE_POST_TO_MARKETPLACE_DEFAULT]: z.boolean(),
  [CbhFeatureFlag.ADMIN_APP_GENERIC_OUTAGE_BANNER]: z.union([
    z.object({}),
    z.object({
      severity: z.nativeEnum(GenericOutageBannerSeverity),
      title: z.string(),
      body: z.string(),
    }),
  ]),
  [CbhFeatureFlag.HIDE_BROWSER_WORKERS]: z.boolean(),
  [CbhFeatureFlag.RELOAD_PAGE_ON_USER_SWITCH]: z.boolean(),
  [CbhFeatureFlag.OVERRIDE_FACILITY_REQUIRED_DOCS_FOR_SHIFT_INVITE]: z.boolean(),
  [CbhFeatureFlag.SHOW_QUIZ_ATTEMPTS]: z.boolean(),
  [CbhFeatureFlag.FACLITY_QUIZ_LAYOUT_AB]: z.string(),
  [CbhFeatureFlag.EARLY_CLOCK_IN_ENABLED_IN_MINUTES]: z.object({
    enabledInMinutes: z.number(),
  }),
  [CbhFeatureFlag.RATING_QUALITIES_OPTIONS]: z.record(z.array(z.string())),
  [CbhFeatureFlag.PLACEMENTS_ACCESS_CONTROL_ROLLOUT]: z.nativeEnum(PlacementAccessControlStatus),
  [CbhFeatureFlag.EXTEND_BOOKABILITY_MODAL_CONFIGURATION]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
      showModalIfShiftStartsWithinHours: z.number(),
      minimumShiftDurationHours: z.number(),
      extendBookabilityByHours: z.number(),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
  [CbhFeatureFlag.ROLLOUT_PARENT_MANAGED_PLACEMENTS]: z.boolean(),
  [CbhFeatureFlag.APP_VERSION_UPDATE_CONTROL]: z.enum(["soft-update", "force-update", "off"]),
  [CbhFeatureFlag.REBRAND_MAX_CONSECUTIVE_HOURS_TO_NO_DOUBLES]: z.boolean(),
  [CbhFeatureFlag.DNR_CONFIG]: z.object({
    reasons: z.array(
      z.object({
        dnrCharacterFloor: z.number(),
        label: z.string(),
        value: z.string(),
        incentives: z.array(z.nativeEnum(DonutDeliveryIncentiveType)).optional(),
      })
    ),
    severity: z
      .object({
        question: z.string(),
        subtext: z.string().optional(),
        response: z.array(
          z.string().refine((value) => ["yes", "no"].includes(value.toLowerCase()), {
            message: "Must be 'yes' or 'no'",
          })
        ),
      })
      .optional(),
  }),
  [CbhFeatureFlag.WORKER_INSTRUCTION_PANEL_VISIBILITY]: z.object({
    hiddenCategories: z.array(z.string()),
  }),
  [CbhFeatureFlag.WORKER_LEFT_EARLY]: z.object({
    enabled: z.boolean().default(false),
    replacementFlow: z.object({
      enabled: z.boolean().default(false),
    }),
    canMarkWLEForVerifiedShifts: z.boolean().default(false),
    canMarkWLEForCancelledShifts: z.boolean().default(false),
    minutesBeforeShiftEndForAllowingReplacement: z.number().default(90),
  }),
  [CbhFeatureFlag.ROLLOUT_JOB_INTERVIEWS]: z.boolean(),
  [CbhFeatureFlag.EVENT_PROMOTION_ALERT]: z.object({
    eventDate: z.string().optional(),
    eventLink: z.string().optional(),
    eventTitle: z.string().optional(),
  }),
  [CbhFeatureFlag.LATE_CANCELLATION_LEAD_TIME_THRESHOLD_IN_HOURS]: z.number(),
  [CbhFeatureFlag.CANCELLATION_REASON_CONFIG]: z.object({
    reasons: z.array(
      z.object({
        minDescriptionLength: z.number().optional(),
        label: z.string(),
        relevantShiftTypes: z.array(z.enum(["filled", "unfilled"])),
      })
    ),
    defaultMinDescriptionLength: z.number(),
  }),
  [CbhFeatureFlag.BOOKED_SHIFT_TIME_PROPOSALS]: z.boolean(),
  [CbhFeatureFlag.VIEW_SHIFT_TABLE]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.DISPLAY_EDIT_NEEDS_BUTTON]: z.boolean(),

  [CbhFeatureFlag.CBH_LITE_TRIAL_BANNER]: z.object({
    mode: z.nativeEnum(CbhLiteTrialState),
    qualification: z.string().optional(),
  }),
  [CbhFeatureFlag.CHHA_REGISTRY_CONFIG]: z.object({
    chhaStatus: z.enum(["certificate", "registry", "unavailable"]),
  }),
  [CbhFeatureFlag.HH_ENABLE_UNSKILLED_QUALIFICATIONS]: z.boolean(),
  [CbhFeatureFlag.SHIFT_INVITE_FRICTION_CONFIG]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(false),
    }),
    z.object({
      enabled: z.literal(true),
      maxAllowedCallOffs: z.number(),
      maxAllowedPercentageOfCallOffs: z.number(),
      numberOfPastDaysToEvaluate: z.number(),
    }),
  ]),
  [CbhFeatureFlag.FACILITY_BACKGROUND_CHECK_UPLOADS]: z.object({
    enabled: z.boolean(),
    statesAndDocumentTitle: z.array(
      z.object({
        stateCode: z.string().nullish(),
        documentName: z.string().nullish(),
        requirementId: z.string().nullish(),
      })
    ),
  }),
  [CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION]: z.object({
    limited: limitedBasketSizeConfigurationSchema,
    warning: warningBasketSizeConfigurationSchema,
  }),
  [CbhFeatureFlag.ENABLE_CHARTING_RECALL]: chartingRecallConfigurationSchema,
  [CbhFeatureFlag.ROLLOUT_EXCLUSION_CANDIDATES_BANNER]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.HIRING_QUALIFICATION_RANKING]: z.array(z.string()),
  [CbhFeatureFlag.ADMIN_SHIFT_TRACKER]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(false),
    }),
    z.object({
      enabled: z.literal(true),
      version: z.number().optional(),
    }),
  ]),
  [CbhFeatureFlag.PRICING_HACKATHON]: z.object({
    variation: z.enum(["on", "off"]),
  }),
  [CbhFeatureFlag.MATRIX_WINDOWING_SEQUENCE]: z.object({
    sequence: z.array(
      z.union([
        z.string(),
        z.object({
          name: z.string(),
          config: z.record(z.unknown()).optional(),
        }),
      ])
    ),
  }),
  [CbhFeatureFlag.CALENDAR_BULK_SHIFT_POSTING]: z.boolean(),
  [CbhFeatureFlag.CALENDAR_SCHEDULE_BULK_POSTING_MULTI_QUALIFICATION]: z.boolean(),
  [CbhFeatureFlag.WAIT_AND_SAVE]: z.object({
    variation: z.enum(["on", "off", "dry-run"]),
  }),
  [CbhFeatureFlag.SIMPLIFIED_SHIFT_CANCELLATION_MODAL]: z.boolean(),
  [CbhFeatureFlag.SCHEDULE_INTERVIEW_BY_WORKPLACE]: z.boolean(),
  [CbhFeatureFlag.FACILITY_DASHBOARD_BANNERS]: z.array(facilityDashboardBannerSchema).default([]),
  [CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED]: z.array(
    z.object({
      type: z.nativeEnum(AnalyticsType),
      embedId: z.string(),
    })
  ),
  [CbhFeatureFlag.PLACEMENT_PERKS_CONFIGURATION]: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      icon: z.string().optional(),
    })
  ),
  [CbhFeatureFlag.CUSTOM_QUALIFICATIONS_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HCP_LICENSE_DEFAULT_STATUS]: z.object({
    activeStatusRules: z.array(
      z.object({
        qualification: z.array(z.string()),
      })
    ),
  }),
  [CbhFeatureFlag.INDEPENDENT_CONTRACTOR_BADGE_REVAMP]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.CANDIDATES_AWAITING_REVIEW_BANNER_AND_BADGE]: z.boolean(),
  [CbhFeatureFlag.ORIENTATION_SHIFTS_CONFIGURATION]: orientationShiftsConfigurationSchema,
  [CbhFeatureFlag.DEFAULT_PLACEMENT_APPLICATION_STATUS_VIEW]: z.string(),
  [CbhFeatureFlag.AUTO_CLOSE_STALE_INTERVIEW_REQUESTS_VIEW]: z.boolean(),
  [CbhFeatureFlag.ENABLE_NEW_GRAD_FRIENDLY_SHIFTS]: z.boolean(),
  [CbhFeatureFlag.STARRED_PROFESSIONAL_CONFIGURATION]: starredProfessionalConfigurationSchema,
  [CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT]: z.object({
    variation: z.enum(["on", "off"]),
    flexAgreementUrl: z.string(),
    hiringAgreementUrl: z.string(),
  }),
  [CbhFeatureFlag.PLACEMENT_SCRAPING_CONFIG]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.PRN_WORKERS_INVITATION_ROLLOUT]: z.boolean(),
  [CbhFeatureFlag.HACKATHON_CONFIGURE_INSTANT_FILL_SHIFTS]: z.object({
    enabled: z.boolean(),
    leadTimeHours: z.number(),
    announcementDialogDelayMs: z.number(),
    faqUrl: z.string(),
  }),
  [CbhFeatureFlag.STAFF_LION_INTEGRATION_CONFIG]: z.object({
    adminUserId: z.string(),
    allowedWorkplaceIds: z.array(z.string()),
  }),
  [CbhFeatureFlag.MISSED_PUNCH_REQUEST]: z.object({
    stage: z.nativeEnum(MissedPunchRequestStage),
    start: z.string().optional(),
  }),
  [CbhFeatureFlag.TOUCHPOINT_INTEGRATION]: z.object({
    enabled: z.boolean(),
  }),
  // this is an array of the requirement ids marked as credentials
  [CbhFeatureFlag.CREDENTIAL_REQUIREMENT_CONFIGURATION]: z.array(z.string()),
  [CbhFeatureFlag.CHARTING_CREDENTIAL_PROVISIONING_CONFIG]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(false),
    }),
    z.object({
      enabled: z.literal(true),
      autoProvisionBeforeShiftThresholdInHours: z.number(),
      organizationCode: z.string(),
      qualificationCodes: z.record(z.enum(CREDENTIAL_PROVISIONABLE_QUALIFICATIONS), z.string()),
      locationCode: z.string(),
    }),
  ]),
  [CbhFeatureFlag.CALENDAR_SEARCH_BY_SHIFT_ID]: z.boolean(),
  [CbhFeatureFlag.ENABLE_JOB_CLOSURE_KEEPING_CANDIDATES]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.SHIFT_GENDER_REQUIREMENT_CONFIG]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.WINTER_STORM_BANNER]: z.boolean(),
  [CbhFeatureFlag.CONFIGURE_VISIT_RATE_SETTING]: z.object({
    maxChargeRate: z.object({
      perHour: z.number(),
      perVisit: z.number(),
    }),
    minChargeRate: z.object({
      perHour: z.number(),
      perVisit: z.number(),
    }),
    takePercent: z.number().min(1).max(99),
    useRateSheet: z.boolean(),
    recommendedChargeRate: visitRecommendedRateParameters.optional(),
  }),
  [CbhFeatureFlag.LOCAL_SHIFT_TIME_OVERRIDE]: z.boolean(),
  [CbhFeatureFlag.RESTRICT_OVERWORK_INVITES_TOGGLE]: z.boolean(),
  [CbhFeatureFlag.CALENDAR_SEARCH_BY_WORKER_ID]: z.boolean(),
  [CbhFeatureFlag.WORKER_REQUESTED_CANCEL_ALERT]: z.boolean(),
  [CbhFeatureFlag.IS_ADMISSION_AND_FIRST_VISIT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.SHIFT_INVITE_PRICE_API]: z.boolean(),
  [CbhFeatureFlag.POST_URGENT_SHIFT]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.ALCHEMY_SHIFT_BOARD_ROLLOUT]: z.boolean(),
  [CbhFeatureFlag.EDIT_MULTIPLE_SHIFTS_VARIABLE_CHARGE]: z.boolean(),
  [CbhFeatureFlag.FIXED_BONUS_BOOSTS]: z.object({
    variation: z.enum(["on", "off"]),
    margin: z.number().min(0).max(1).optional(),
  }),
  [CbhFeatureFlag.IN_HOME_LOCK_ENABLED]: z.boolean(),
  [CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH]: z.boolean(),
  [CbhFeatureFlag.PROBLEMATIC_REQUIREMENTS_BANNER]: z
    .array(
      z.object({
        workplaceId: objectId,
        requirements: z.array(z.string().min(1)).min(1),
      })
    )
    .default([]),
  [CbhFeatureFlag.DOCS_V2_TOOLS_IN_ADMIN_UI]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.SMS_PORTAL_ENABLED]: z.boolean(),
  [CbhFeatureFlag.FCF_UNBOOKED_SHIFT_SWAP]: z.boolean(),
  [CbhFeatureFlag.CLIPBOARD_ROSTER_ENABLED]: z.boolean(),
  [CbhFeatureFlag.ROSTER_WINDOW_REDESIGN_ENABLED]: z.object({ enabled: z.boolean() }),
  [CbhFeatureFlag.ROSTER_STALE_REMINDER]: z.boolean(),
  [CbhFeatureFlag.ENABLE_SHIFT_TRACKER]: z.boolean(),
  [CbhFeatureFlag.EXTEND_SHIFT_RELEASE]: extendShiftReleaseSchema,
  [CbhFeatureFlag.LIGHT_DARK_MODE_TOGGLE]: z.boolean(),
  [CbhFeatureFlag.HIRING_MANAGER_ONBOARDING_WELCOME]: z.boolean(),
  [CbhFeatureFlag.SHOW_RUSH_FEE_SCHEDULE_IN_WORKPLACE_DRAWER]: z.boolean(),
  [CbhFeatureFlag.CHECK_TIERED_RUSH_FEE_SCHEDULE_IN_CONFIRMATION_DIALOG]: z.boolean(),
  [CbhFeatureFlag.ENABLE_HHH_HCP_PROFILE_V2]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_RATING_PROMPT]: z.boolean(),
  [CbhFeatureFlag.HOME_HEALTH_WORKPLACE_MOBILE_EXPERIENCE_ENABLED]: z.boolean(),
  [CbhFeatureFlag.VARIABLE_CHARGE_BANNER_MEMORIAL_DAY]: z.boolean(),
  [CbhFeatureFlag.ROSTER_OVERTIME_CONFIG]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
      // Workplace-wide weekly max hours. `-1` means "no maximum".
      defaultMaxHours: z.union([z.literal(-1), z.number().nonnegative()]),
    }),
    z.object({
      enabled: z.literal(false),
    }),
  ]),
  [CbhFeatureFlag.CLASSIC_CALENDAR_EXCEPTION]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_ANALYTICS_TABLES]: workplaceAnalyticsFeatureFlagSchema,
  [CbhFeatureFlag.ADMIN_ASSISTANT_AI_PREVIEW]: z.boolean(),
  [CbhFeatureFlag.ENABLE_CANCELLATION_NOTE]: z.boolean(),
  [CbhFeatureFlag.ZENDESK_HELP_CENTER_SSO_ENABLED]: z.boolean(),
  [CbhFeatureFlag.SCHEDULE_VIEW_POST_LAUNCH]: z.boolean(),
  [CbhFeatureFlag.SCHEDULE_VIEW_MULTI_INVITES]: z.boolean(),
  [CbhFeatureFlag.SCHEDULER_AGENT_EXPERIMENT]: schedulerAgentFeatureFlagSchema,
  [CbhFeatureFlag.DNR_FEEDBACK_NUDGE]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_SKILL_FEEDBACK]: z.boolean(),
  [CbhFeatureFlag.ADMIN_IDENTITY_VERIFICATION]: z.boolean(),
  [CbhFeatureFlag.CHILD_WORKPLACES_SHIFT_REPORT_EXPORT]: z.boolean(),
  [CbhFeatureFlag.BADGE_STRIKE_BLOCK]: z.boolean(),
  [CbhFeatureFlag.WORKPLACE_FEEDBACK_PROMPT]: workplaceFeedbackPromptFeatureFlagSchema,
} as const;

export type CbhFeatureFlags = {
  [K in keyof typeof cbhFeatureFlags]: z.infer<(typeof cbhFeatureFlags)[K]>;
};
/* eslint-enable max-lines */
